// Pure, framework-free helpers for role / team-lead / demo logic.

import { isSuperAdminOwner } from "./brokerageSettings";

// ---- The plain checks (system cleanup 2026-09-24, Part A; Part D adds the rest) ----
// Roles are the stored values: Admin = Team Lead, Supervisor = Client Care, Employee = Agent.
// None of the role checks looks at active; pair them with isActiveUser / activeUsers.
// Server twin: functions-api/roles.js, same names, same answers.
// type is a number in the app, but a value saved as text ("2") must still count as off
// (accountWatch.js already defends this way). Settled after the Task 1-2 review, 09-24.
export const isActiveUser = (u) => !!u && Number(u.type) !== 1 && Number(u.type) !== 2;
export const activeUsers = (list) => (Array.isArray(list) ? list.filter(isActiveUser) : []);
export const isTeamLead = (u) => !!u && u.role === "Admin";
export const isClientCareRole = (u) => !!u && u.role === "Supervisor";
export const isManager = (u) => isTeamLead(u) || isClientCareRole(u);

// The one name map (Part D). Stored role on the left, the name the team uses on the right.
// It sits up here because DEMO_VIEW_ROLES below calls roleLabel while the module loads.
export const ROLE_LABELS = Object.freeze({
  Admin: "Team Lead",
  Supervisor: "Client Care",
  Employee: "Agent",
  Accountant: "Accountant",
});
export const roleLabel = (role) =>
  Object.prototype.hasOwnProperty.call(ROLE_LABELS, role) ? ROLE_LABELS[role] : String(role || "");

// True if an Admin (team lead) can currently be PICKED as team lead on a new file.
// Excludes: non-Admins, deleted (type 1), disabled (type 2),
// explicitly switched-off team leads (activeteamlead === false), and the demo account.
// A missing activeteamlead field counts as ON, so existing team leads stay selectable.
export const isBookableTeamLead = (user) => {
  if (!isTeamLead(user) || !isActiveUser(user)) return false;
  if (user.activeteamlead === false) return false;
  if (user.isDemo === true) return false;
  return true;
};

// The 10 granular permission keys a Client Care (Supervisor) user can hold.
export const SUPERVISOR_ACCESS_KEYS = [
  "approveDeal",
  "deleteDeal",
  "underContract",
  "pending",
  "closed",
  "closedVolume",
  "pendingVolume",
  "netVolume",
  "commissionVolume",
  "pendingCommission",
];

// Does this person hold one of these roles? The owner passes whatever his role says,
// so every door in the app (the route guard in pages/dashboard.jsx, the sidebar, the
// Settings tabs, the Integrations sub-tabs) opens for him the same way. A role question
// only: deleted and disabled logins are stopped at sign-in (App.js) and by the database
// (firestore.rules onTeam), not here.
export function hasRole(user, roles) {
  if (!user) return false;
  if (isSuperAdminOwner(user)) return true;
  return Array.isArray(roles) && roles.includes(user.role);
}

// Single source of truth for "may this person see commission figures?"
//
// Commission visibility was a role check copy-pasted across ~11 files, while
// the `commissionVolume` permission was never read by any code — so the toggle
// in User Management implied protection it didn't actually provide. Everything
// that displays a commission amount should call this instead.
//
//   - The owner: always, whatever his role says (utils/owner.js).
//   - Team Lead (Admin) and Accountant: always.
//   - Client Care (Supervisor): only when granted, per person.
//   - Agents (Employee): never for other people. They still see their OWN
//     commission on their own deals; that is not gated by this.
export function canSeeCommissions(user) {
  if (!isActiveUser(user)) return false;
  if (isSuperAdminOwner(user)) return true;
  if (isTeamLead(user) || user.role === "Accountant") return true;
  if (isClientCareRole(user)) {
    return Boolean(user.access && user.access.commissionVolume);
  }
  return false;
}

// Client Care never has commissions on screen by default, even when granted.
// They must reveal them, and that reveal resets on every login, so a permitted
// Client Care user doesn't sit with commission figures showing all day.
// Team Lead and Accountant are always-on and never see a reveal toggle.
export function requiresCommissionReveal(user) {
  return Boolean(user && user.role === "Supervisor");
}

// The roles the demo account can preview (never Super Admin).
export const DEMO_VIEW_ROLES = ["Admin", "Supervisor", "Employee", "Accountant"].map(
  (value) => ({ value, label: roleLabel(value) }),
);

// True if this user is the demo / preview account.
export const isDemoUser = (user) => Boolean(user && user.isDemo === true);

// An all-true Client Care permission map, so the demo "Client Care" view hides nothing.
export const fullSupervisorAccess = () =>
  SUPERVISOR_ACCESS_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

// The "two hats" rule: does this person do Client Care DUTIES? True for
// active Client Care (Supervisor) and for active Agents with the owner-set
// "Client care duties" switch (users.isClientCare). DUTIES ONLY — never use
// this for commission visibility (canSeeCommissions above stays the one
// money rule, and it deliberately does NOT consult the flag: Henry's call
// 2026-07-31 is that flagged agents never see others' figures, which also
// means they cannot approve sale turn-ins).
export function actsAsClientCare(user) {
  if (!isActiveUser(user)) return false;
  if (isClientCareRole(user)) return true;
  return user.role === "Employee" && user.isClientCare === true;
}

// May this user search ALL team contacts (not just their own) when filling
// out forms? Staff who process files for other agents: the Super Admin owner,
// anyone flagged Listing/Transaction Coordinator, and Client Care.
// One place to change if eligibility ever widens.
export function canSearchTeamContacts(user) {
  if (!isActiveUser(user)) return false;
  if (isSuperAdminOwner(user)) return true;
  if (user.isListingCoordinator === true) return true;
  if (user.isTransactionCoordinator === true) return true;
  return actsAsClientCare(user);
}

// Returns a COPY of the demo user with its effective role overridden for preview.
// Keeps identity + isDemo intact so the "View as" switcher stays visible.
// Never writes to Firestore.
export const applyDemoRole = (user, role) => {
  const next = { ...user, role, isDemo: true };
  if (role === "Supervisor") {
    next.access = fullSupervisorAccess();
  }
  return next;
};

// ---- The owner's View As (09/28/2026) ----
// Henry trains staff by seeing the app exactly as a Team Lead, Client Care, Agent or
// Accountant sees it. Only the copy of the user held in memory changes: nothing is
// written to Firestore, nothing is remembered on the device (App.js remembers the role
// from the fresh record only), and a reload returns him to his own view. While
// viewAsRole is set, isSuperAdminOwner (utils/owner.js) answers false, so his owner-only
// items vanish too; canUseViewAs keeps the switch and the banner on screen.
export const VIEW_AS_ROLES = DEMO_VIEW_ROLES;

// True while the owner is viewing the app as another role.
export const isViewingAs = (user) => Boolean(user && user.viewAsRole);

// A COPY of the user seen as `role`. His real role and permissions are kept on the copy
// (realRole, realAccess) so clearViewAs can put them back. Switching from one viewed role
// to another keeps the ORIGINAL real role and permissions. Coming from a person view, it
// starts again from his own record.
export const applyViewAs = (userIn, role) => {
  const user = userIn && userIn.realUser ? userIn.realUser : userIn;
  const viewing = isViewingAs(user);
  const realRole = user.realRole || user.role;
  const realAccess = viewing ? user.realAccess : user.access;
  return {
    ...user,
    role,
    viewAsRole: role,
    realRole,
    realAccess,
    access: role === "Supervisor" ? fullSupervisorAccess() : realAccess,
  };
};

// A COPY of the user back in his own view: real role and permissions restored, and the
// View As fields dropped. A user who is not viewing as comes back unchanged. From a person
// view, his own record comes back exactly as it was.
export const clearViewAs = (user) => {
  if (!user || !isViewingAs(user)) return user;
  if (user.realUser) return user.realUser;
  // eslint-disable-next-line no-unused-vars
  const { viewAsRole, realRole, realAccess, ...rest } = user;
  const next = { ...rest, role: realRole || rest.role };
  if (realAccess === undefined) delete next.access;
  else next.access = realAccess;
  return next;
};

// ---- View As a specific PERSON (09/28/2026) ----
// Henry also sees the app exactly as one team member sees it: the user held in memory
// becomes a COPY of that person's users record (their id, role, permissions, switches like
// isClientCare / listingCoordinatorId, their name), so every screen that filters by the
// signed-in person's id shows their files, checklist and counts. His own full record rides
// along as realUser, for clearViewAs and canUseViewAs (utils/owner.js). The database still
// knows HIM, so this view is look only: utils/lookOnly.js stops every save while
// viewAsPersonId is set. Memory only; a reload returns him to his own view.

// True while the owner is viewing the app as a specific person.
export const isViewingAsPerson = (user) => Boolean(user && user.viewAsPersonId);

// Who can be picked: every active team member in the four roles, minus the owner himself
// and the demo account.
export const viewAsPeople = (users, realUser) => {
  const selfId = realUser && realUser.id;
  const roles = VIEW_AS_ROLES.map((r) => r.value);
  return activeUsers(users).filter(
    (u) => u && u.id && u.id !== selfId && roles.includes(u.role) && !isDemoUser(u),
  );
};

// The same people grouped by role label, in the menu's role order, each group by name.
export const viewAsPeopleByRole = (users, realUser) => {
  const people = viewAsPeople(users, realUser);
  const name = (u) => `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email || u.id;
  return VIEW_AS_ROLES.map((r) => ({
    role: r.value,
    label: r.label,
    people: people
      .filter((u) => u.role === r.value)
      .map((u) => ({ id: u.id, name: name(u) }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
  })).filter((g) => g.people.length > 0);
};

// A COPY of `person` as the signed-in user, with the owner's real record kept on it.
// Switching from any view (a role or another person) keeps his ORIGINAL record.
export const applyViewAsPerson = (user, person) => {
  const realUser = user && user.realUser ? user.realUser : clearViewAs(user);
  return {
    ...person,
    viewAsPersonId: person.id,
    viewAsRole: person.role,
    realUser,
  };
};

// Who may Reopen a completed open house once the ten seconds are up? Team Lead
// (role "Admin") and the owner, and nobody else — for everyone else Henry's
// 08/02/2026 "closed means closed" rule still takes over at that moment. The
// pair is the one already used for a deal's timeline
// (utils/timelinePermissions.js: isSuperAdminOwner OR role "Admin"), so there
// is no second definition of "Team Lead" in the app.
// It lives in roles.js rather than beside the other open-house helpers because
// openHouseHelpers.js has no imports and is reached by a public, pre-sign-in
// page; this check needs isSuperAdminOwner, which reaches Firebase.
// Note this is NOT who may press Undo: within UNDO_WINDOW_MS anyone signed in
// may, which is why the Undo bar never consults this.
// Deleted (type 1) and disabled (type 2) accounts are turned away first, like
// the neighbours above (review fix, 09/16/2026): sign-in already stops them,
// so this only matters for a Team Lead switched off while a tab was open.
export function canReopenOpenHouse(user) {
  if (!isActiveUser(user)) return false;
  if (isSuperAdminOwner(user)) return true;
  return isTeamLead(user);
}
